import { Link } from 'react-router-dom';
import { Shell } from '../components/Layout.js';
import { useDocumentMeta } from '../lib/document-meta.js';
import { useT } from '../state/i18n.js';

const JEVRAG_URL = 'https://github.com/cloudbtl/JevRAG';
const SPEC_URL = 'https://github.com/cloudbtl/cloudbtl-site/blob/main/docs/spec/landing-layer.md';

const FAQ_LD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What is a decision foundation model, and why does CloudBTL fit it?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A decision foundation model answers typed questions — choose one of these, score this 0–3, yes or no — over a state you give it, instead of generating text. It is fast, cheap and auditable only when the options are well described. CloudBTL stores each document with descriptors (what it contains, under which conditions, from which page) and serves them as exactly those options.',
      },
    },
    {
      '@type': 'Question',
      name: 'Does this replace RAG or embeddings?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'No. Embeddings are one descriptor among many. The difference is that a described document can also be filtered, counted and compared, and every choice points back to the page it came from.',
      },
    },
    {
      '@type': 'Question',
      name: 'What file types can I land or share?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Signed-in users can land any format in Smartlake, including folder drops and direct uploads up to 2 GB. Uploads that immediately create tracked share links are limited to 50 MB.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do recipients need an account?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'No. Public links open immediately. Passcode and email-domain links require the recipient to enter a passcode or use an allowed email domain before opening.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is tracked?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Per-page dwell time, opens, total session count, anonymous visitor identifiers, and optional explicit clicks. CloudBTL does not collect names or company info from recipients unless a domain-gated link requires it.',
      },
    },
  ],
};

export function LandingPage(): JSX.Element {
  const t = useT();
  useDocumentMeta({
    title: t({
      ko: 'CloudBTL — 결정 모델을 위한 문서 계층',
      en: 'CloudBTL — The document layer for decision foundation models',
    }),
    description: t({
      ko: '어떤 파일이든 던지세요. CloudBTL은 문서마다 무엇을 담고 있고, 어떤 조건이고, 어느 페이지에서 왔는지를 보관해, 결정 모델에게 가장 가까운 것 대신 잘 설명된 선택지를 줍니다. 추적되는 공유 링크도 그대로.',
      en: 'Land any file. CloudBTL keeps what each document contains, under which conditions, from which page — and hands your decision foundation model well-described options instead of nearest neighbors. Tracked sharing included.',
    }),
    canonical: '/',
  });

  return (
    <Shell>
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(FAQ_LD) }}
      />
      <article>
        <header className="hero hero--product">
          <div className="hero-copy">
            <p className="hero-kicker">
              {t({
                ko: '결정 모델을 위한 문서 계층',
                en: 'The document layer for decision models',
              })}
            </p>
            <h1>{t({ ko: '파일을 선택지로 만드세요.', en: 'Turn files into choices.' })}</h1>
            <p>
              {t({
                ko: '어떤 파일이든 던지면 CloudBTL이 원본과 폴더 경로를 보존하고, 무엇이 들어 있는지 설명하는 디스크립터를 쌓습니다. Jev 같은 결정 모델은 그 설명을 읽고 필요한 자료와 다음 행동을 고릅니다.',
                en: 'Land any file. CloudBTL preserves the original and its folder path, then builds descriptors of what it contains. Decision models such as Jev use those descriptions to choose evidence and the next action.',
              })}
            </p>
            <div className="hero-cta">
              <Link to="/lake" className="cta-primary">
                {t({ ko: 'Smartlake 열기', en: 'Open Smartlake' })}
              </Link>
              <Link to="/app" className="cta-secondary">
                {t({ ko: '추적 링크 만들기', en: 'Create a tracked link' })}
              </Link>
            </div>
            <p className="hero-links">
              <a href={JEVRAG_URL} target="_blank" rel="noreferrer">
                {t({ ko: 'JevRAG 참조 구현', en: 'JevRAG reference implementation' })}
              </a>
              {' · '}
              <a href={SPEC_URL} target="_blank" rel="noreferrer">
                {t({ ko: '열린 원천층 계약', en: 'Open landing-layer contract' })}
              </a>
            </p>
          </div>
          <div
            className="hero-console"
            aria-label={t({ ko: 'CloudBTL 처리 흐름 예시', en: 'CloudBTL processing example' })}
          >
            <div className="hero-console__bar">
              <span>Smartlake</span>
              <span className="hero-console__live">LIVE</span>
            </div>
            <div className="hero-file">
              <span className="hero-file__icon">PDF</span>
              <span>
                <strong>SEI Tower Rent Roll</strong>
                <small>Drive / LM / Leasing · 18 pages</small>
              </span>
              <em>{t({ ko: '저장됨', en: 'landed' })}</em>
            </div>
            <div className="hero-descriptors">
              <span>doc.type · rent roll</span>
              <span>period · 2026-03</span>
              <span>entities · 24 tenants</span>
              <span>source · page 4–18</span>
            </div>
            <div className="hero-decision">
              <small>{t({ ko: '질문', en: 'Question' })}</small>
              <strong>
                {t({ ko: '3월 임차인별 보증금과 임대료', en: 'March deposits and rent by tenant' })}
              </strong>
              <div>
                <span>folders / Leasing</span>
                <b>2.7</b>
              </div>
              <div className="is-picked">
                <span>SEI Tower Rent Roll</span>
                <b>3.0</b>
              </div>
              <div>
                <span>monthly sales report</span>
                <b>0.8</b>
              </div>
            </div>
          </div>
        </header>

        <section
          className="product-strip"
          aria-label={t({ ko: '제품 원칙', en: 'Product principles' })}
        >
          <span>
            <strong>SHA-256</strong>
            {t({ ko: '내용 중복 제거', en: 'content deduplication' })}
          </span>
          <span>
            <strong>Source path</strong>
            {t({ ko: '원래 폴더 보존', en: 'folder structure preserved' })}
          </span>
          <span>
            <strong>Multi-tree</strong>
            {t({ ko: '논리 구조는 여러 개', en: 'many logical views' })}
          </span>
          <span>
            <strong>Provenance</strong>
            {t({ ko: '페이지까지 되짚기', en: 'trace every fact' })}
          </span>
        </section>

        <section aria-labelledby="pillars-heading" className="features">
          <h2 id="pillars-heading" className="visually-hidden">
            {t({ ko: 'CloudBTL이 하는 일', en: 'What CloudBTL does' })}
          </h2>
          <article>
            <h3>{t({ ko: '던지면 남는다', en: 'Land it, keep it' })}</h3>
            <p>
              {t({
                ko: '해시로 중복을 걷고, 출처·원래 폴더 경로·묶음·버전을 보존합니다. Drive를 옮기지 않고도 같은 구조를 논리 트리로 가져올 수 있습니다.',
                en: 'Deduplicate by hash and preserve source, original folder path, batch, and version. Mirror a Drive hierarchy as a logical tree without moving it.',
              })}
            </p>
          </article>
          <article>
            <h3>{t({ ko: '조건이 붙은 사실', en: 'Facts with conditions' })}</h3>
            <p>
              {t({
                ko: '텍스트 추출은 기본. 금액·기간·당사자 같은 값은 VAT 포함 여부나 기준일 같은 조건과 그 값이 나온 페이지를 함께 기록합니다. 사람이 근거를 확인할 수 있습니다.',
                en: 'Text extraction is built in. Values like amounts, terms and parties carry their conditions — VAT inclusion, reference date — and the page they came from. A person can check every answer.',
              })}
            </p>
          </article>
          <article>
            <h3>{t({ ko: '고르고, 그다음 계산', en: 'Choose, then compute' })}</h3>
            <p>
              {t({
                ko: '결정 모델은 자료와 다음 행동을 고르고, 걸러 세고 비교하는 일은 코드가 합니다. 새 문서는 서명된 웹훅으로 알리고, 당신의 파이프라인은 하나의 API로 결과를 되돌려 씁니다. 임베딩도 그 결과 중 하나일 뿐입니다.',
                en: 'A decision model picks the source and the next action; code does the filtering and counting. New documents are announced over signed webhooks and your pipelines write results back through one API. Embeddings are just one of those results.',
              })}
            </p>
          </article>
        </section>

        <section aria-labelledby="sharing-heading" className="decision">
          <h2 id="sharing-heading">
            {t({ ko: '공유는 그대로, 추적되게', en: 'Sharing, still tracked' })}
          </h2>
          <p className="decision-lead">
            {t({
              ko: '문서를 사람에게 보낼 때도 CloudBTL을 쓰세요. 누가 열었고 어디서 멈췄는지가 남고, 그 열람 기록은 다시 모델의 신호가 됩니다.',
              en: 'When a document goes to a person, send it through CloudBTL too. Who opened it and where they stopped is recorded, and that reading signal feeds back to your models.',
            })}
          </p>
          <div className="features">
            <article>
              <h3>{t({ ko: '읽기 깊이', en: 'Reading depth' })}</h3>
              <p>
                {t({
                  ko: '페이지별 체류 시간과 어디서 이탈하는지 확인하세요.',
                  en: 'See per-page dwell time and where readers drop off.',
                })}
              </p>
            </article>
            <article>
              <h3>{t({ ko: '대상별 링크', en: 'Audience links' })}</h3>
              <p>
                {t({
                  ko: '대상마다 다른 링크를 발급하세요. 비밀번호, 이메일 도메인, 만료일 설정.',
                  en: 'Issue different links for different audiences. Passcode, email domain, or expiry.',
                })}
              </p>
            </article>
            <article>
              <h3>{t({ ko: '후속 타이밍', en: 'Follow-up timing' })}</h3>
              <p>
                {t({
                  ko: '관심 높은 고객이 문서에 다시 돌아오면 알림을 받으세요.',
                  en: 'Get notified when a hot prospect comes back to the document.',
                })}
              </p>
            </article>
          </div>
        </section>

        <section aria-labelledby="faq-heading" className="faq">
          <h2 id="faq-heading">{t({ ko: '자주 묻는 질문', en: 'Common questions' })}</h2>
          <dl>
            <dt>
              {t({
                ko: '결정 모델(decision foundation model)이 뭐고, 왜 CloudBTL과 맞나요?',
                en: 'What is a decision foundation model, and why does CloudBTL fit it?',
              })}
            </dt>
            <dd>
              {t({
                ko: '텍스트를 생성하는 대신, 주어진 상태 위에서 "이 중 하나를 골라", "0~3점으로 평가해", "예/아니오" 같은 유형이 정해진 질문에 답하는 모델입니다. 선택지가 잘 설명되어 있을 때만 빠르고 싸고 감사 가능합니다. CloudBTL은 문서마다 무엇을 담고 있고 어떤 조건이고 어느 페이지에서 왔는지를 디스크립터로 보관하고, 그걸 바로 그 선택지로 제공합니다.',
                en: 'A model that answers typed questions — choose one of these, score this 0–3, yes or no — over a state you give it, instead of generating text. It is fast, cheap and auditable only when the options are well described. CloudBTL stores each document with descriptors (what it contains, under which conditions, from which page) and serves them as exactly those options.',
              })}
            </dd>
            <dt>
              {t({ ko: 'RAG나 임베딩을 대체하나요?', en: 'Does this replace RAG or embeddings?' })}
            </dt>
            <dd>
              {t({
                ko: '아니요. 임베딩도 여러 디스크립터 중 하나입니다. 다른 점은, 설명된 문서는 걸러 세고 비교할 수도 있고, 모든 선택이 그 값이 나온 페이지로 되짚어진다는 것입니다.',
                en: 'No. Embeddings are one descriptor among many. The difference is that a described document can also be filtered, counted and compared, and every choice points back to the page it came from.',
              })}
            </dd>
            <dt>
              {t({
                ko: '어떤 파일을 던지거나 공유할 수 있나요?',
                en: 'What file types can I land or share?',
              })}
            </dt>
            <dd>
              {t({
                ko: 'Smartlake에는 로그인 후 어떤 형식이든 올릴 수 있고, 파일·폴더 드롭과 2GB 직접 업로드를 지원합니다. 추적 링크로 바로 공유하는 업로드는 50MB까지입니다.',
                en: 'Signed-in users can land any format in Smartlake, including folder drops and direct uploads up to 2 GB. Uploads that immediately create tracked share links are limited to 50 MB.',
              })}
            </dd>
            <dt>
              {t({ ko: '받는 사람도 계정이 필요한가요?', en: 'Do recipients need an account?' })}
            </dt>
            <dd>
              {t({
                ko: '아니요. 공개 링크는 바로 열립니다. 비밀번호·이메일 도메인 링크는 열기 전에 비밀번호를 입력하거나 허용된 이메일 도메인을 사용해야 합니다.',
                en: 'No. Public links open immediately. Passcode and email-domain links require the recipient to enter a passcode or use an allowed email domain before opening.',
              })}
            </dd>
            <dt>{t({ ko: '무엇이 추적되나요?', en: 'What is tracked?' })}</dt>
            <dd>
              {t({
                ko: '페이지별 체류 시간, 열람, 총 세션 수, 익명 방문자 식별자, 선택적 클릭. 도메인 제한 링크가 요구하지 않는 한 CloudBTL은 받는 사람의 이름이나 회사 정보를 수집하지 않습니다.',
                en: 'Per-page dwell time, opens, total session count, anonymous visitor identifiers, and optional explicit clicks. CloudBTL does not collect names or company info from recipients unless a domain-gated link requires it.',
              })}
            </dd>
          </dl>
        </section>
      </article>
    </Shell>
  );
}
